import { RouterOutputs } from '@crocodille/api';
import { assert, Maybe } from '@crocodille/util';
import { useCallback } from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { useApp } from '../app-provider';
import { laggy } from '../util/swr';
import { getOrderKey, useOrder } from './order.v2';

export type Payment = NonNullable<
  NonNullable<RouterOutputs['order']['byTokenValue']>['payment']
>;

export type GooglePayConfiguration =
  RouterOutputs['payment']['configurationGooglePay'];

export function isPaymentPayed(payment: Maybe<Payment>): payment is Payment {
  return Boolean(
    payment?.state && ['authorized', 'completed'].includes(payment?.state),
  );
}

export function isPaymentCancelled(
  payment: Maybe<Payment>,
): payment is Payment {
  return Boolean(payment?.state && payment?.state === 'cancelled');
}

export function isPaymentAwaitingPayment(
  payment: Maybe<Payment>,
): payment is Payment {
  return Boolean(
    payment?.state && ['new', 'processing'].includes(payment?.state),
  );
}

export interface GooglePayPayload {
  readonly details: google.payments.api.PaymentData;
}

export interface ApplePayPayload {
  readonly paymentData: unknown;
}

export function useOrderPayment(tokenValue: Maybe<string> | false) {
  const { trpc } = useApp();

  const { data: order, isLoading, isValidating, error } = useOrder(tokenValue);

  //
  // Card payment
  //

  const { trigger: payByCard, isMutating: isMutatingCard } = useSWRMutation(
    getOrderKey(tokenValue),
    async ([_, tokenValue]) => {
      const potentiallyPayedOrder = await trpc.payment.payByCard.mutate({
        tokenValue,
      });

      return potentiallyPayedOrder;
    },
  );

  //
  // Google pay
  //

  const { trigger: payByGooglePayMutation, isMutating: isMutatingGooglePay } =
    useSWRMutation(
      getOrderKey(tokenValue),
      async ([_, tokenValue], { arg }: { arg: GooglePayPayload }) => {
        const potentiallyPayedOrder = await trpc.payment.payByGooglePay.mutate({
          tokenValue,
          details: arg.details,
        });

        return potentiallyPayedOrder;
      },
    );

  const payByGooglePay = useCallback(
    async ({ details }: GooglePayPayload) => {
      const order = await payByGooglePayMutation({ details });

      assert(order?.payment?.paymentMethodCode === 'google_pay');

      return order.payment.redirectUrl
        ? 'unknown' // When redirect comes from backend, we don't know id payment was successful yet.
        : 'success';
    },
    [payByGooglePayMutation],
  );

  //
  // Apple pay
  //

  const { trigger: payByApplePayMutation, isMutating: isMutatingApplePay } =
    useSWRMutation(
      getOrderKey(tokenValue),
      async (
        [_, tokenValue],
        { arg: { paymentData } }: { arg: ApplePayPayload },
      ) => {
        const potentiallyPayedOrder = await trpc.payment.payByApplePay.mutate({
          tokenValue,
          paymentData,
        });

        return potentiallyPayedOrder;
      },
    );

  const payByApplePay = useCallback(
    async ({ paymentData }: ApplePayPayload) => {
      const order = await payByApplePayMutation({ paymentData });

      assert(order?.payment?.paymentMethodCode === 'apple_pay');

      return order.payment.redirectUrl
        ? 'unknown' // When redirect comes from backend, we don't know id payment was successful yet.
        : 'success';
    },
    [payByApplePayMutation],
  );

  const validateApplePayMerchant = useCallback(
    async (validationUrl: string): Promise<unknown> => {
      const result = await trpc.payment.validateApplePayMerchant.mutate({
        validationUrl,
      });

      return result;
    },
    [order?.tokenValue],
  );

  //
  // Twisto
  //

  const payByTwisto = useCallback(async () => {
    throw new Error('Not implemented');
  }, []);

  const payment = order?.payment;

  const isAwaitingPayment = isPaymentAwaitingPayment(payment);

  const paymentGatewayRedirectUrl =
    isAwaitingPayment &&
    (payment?.paymentMethodCode === 'card' ||
      payment?.paymentMethodCode === 'google_pay' ||
      payment?.paymentMethodCode === 'apple_pay')
      ? payment?.redirectUrl
      : undefined;

  const isMutating =
    isMutatingCard || isMutatingGooglePay || isMutatingApplePay;

  return {
    order,
    payment,
    isPayed: isPaymentPayed(payment),
    isCancelled: isPaymentCancelled(payment),
    isAwaitingPayment,
    paymentGatewayRedirectUrl,
    isLoading,
    isValidating,
    isMutating,
    error,
    payByCard,
    payByGooglePay,
    payByApplePay,
    payByTwisto,
    validateApplePayMerchant,
  };
}

export function useApplePayConfiguration() {
  const { trpc } = useApp();

  const response = useSWR(
    'payment.configurationApplePay',
    async () => {
      const config = await trpc.payment.configurationApplePay.query();

      return config;
    },
    { use: [laggy], revalidateOnMount: true },
  );

  return response;
}

export function useGooglePayConfiguration() {
  const { trpc } = useApp();

  const response = useSWR(
    'payment.configurationGooglePay',
    async () => {
      const config = await trpc.payment.configurationGooglePay.query();

      return config;
    },
    { use: [laggy], revalidateOnMount: true },
  );

  return response;
}

export function createGooglePayMethodData(googlePay: GooglePayConfiguration) {
  return {
    environment: googlePay.environment,
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods:
            googlePay.allowedCardAuthMethods as google.payments.api.CardAuthMethod[],
          allowedCardNetworks:
            googlePay.allowedCardNetworks as google.payments.api.CardNetwork[],
        },
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: googlePay.gateway,
            gatewayMerchantId: googlePay.gatewayMerchantId,
          },
        },
      },
    ],
    merchantInfo: {
      merchantId: googlePay.merchantId,
      merchantName: googlePay.merchantName,
    },
  } as Omit<google.payments.api.PaymentDataRequest, 'transactionInfo'>;
}

export function usePaymentMethods() {
  const { locale, trpc } = useApp();

  return useSWR(
    ['payment.paymentMethods', { locale }],
    async ([_, { locale }]) => {
      const data = await trpc.payment.paymentMethods.query({ locale });

      return data;
    },
  );
}
